// @ts-strict-ignore
import {
  ActionType,
  WorkflowTransformationIssueType,
  importedDataPassingActionOptionKeys,
  inputOutputAttributeActionOptionKeys,
  matrixModelActionOptionKeys,
  outputAttributeActionOptionKeys,
  snippetChildActionOptionKeys,
} from '../../constants';
import type {
  Tag,
  Action,
  LegacyAction,
  Outcome,
  LegacyActionOption,
  LegacyNestedInputAttributeActionOption,
  LegacyNestedOutputAttributeActionOption,
  TagObjectInAction,
  TransformIssue,
  ServiceObjectInAction,
  OutcomeObjectInAction,
  InputObjectInAction,
  OutputObjectInAction,
  ObjectIdTransformationMap,
  LegacyNestedMatrixModelActionOption,
  LegacyMatrixModelResponse,
  List,
  LegacyImportedDataPassingActionOption,
  ActionOption,
  ImportedDataPassingObject,
  WorkflowObjectInAction,
  ExitObjectInAction,
  PublishedAttribute,
  SnippetObjectInAction,
  WorkflowProps,
} from '../../types';
import { remapExpressionDataRouteIds, remapListIdsForListServiceAction } from '../../utils';
import { generatePublicId } from '../../../../service/publicIds';
import { detokenize } from '~/flow/expressionHelpers';
import transformBooleanRule from '../rules';
import {
  getAxisOrder,
} from '~/dashboard/views/workflows/service/actions/matrixModels';
import { DEFAULT_TAG_COLOR_IN_HEX_FORMAT } from '~/dashboard/views/workflows/editor/components/constants';
import { LIST_SERVICE_KEY_IN_SERVICES } from '~/dashboard/views/workflows/service/services/constants';
import type { CustomList } from 'flow/service/actions/services/customLists/types';
import { CUSTOM_LIST_COLUMNS_OPTION_KEY, CUSTOM_LIST_TOKEN_OPTION_KEY, CUSTOM_LIST_VERSION_OPTION_KEY } from 'flow/service/actions/services/customLists/constants';
import { SNIPPET_PUBLIC_VERSION_NUMBER_KEY, SNIPPET_TOKEN_KEY } from 'flow/service/actionOptions';
import { createContainerContextForActionFromOptions, containsSnippetRemoteActionMap } from 'flow/service/actions/snippets';
import { formatEmptyMatrixValuesByType } from 'flow/service/actions/matrixModels/formatters';

export function getFormattedTagData(
  legacyTag: Tag,
  action = {} as LegacyAction,
): TagObjectInAction {
  return {
    name: legacyTag?.tag_name || action.name,
    color: legacyTag?.tag_color || action?.tagColor || DEFAULT_TAG_COLOR_IN_HEX_FORMAT,
    description: legacyTag?.description || null,
    categoryId: legacyTag?.category_id || null,
    archived: legacyTag?.archived ?? false,
    categoryName: legacyTag?.category_name || null,
    isCaseCreating: legacyTag?.is_case_creating ?? false,
    isAdverseActionReason: legacyTag?.is_adverse_action_reason ?? false,
    suppressionInterval: legacyTag?.suppression_interval || null,
    outcomes: legacyTag?.outcomes?.map(tagOutcome => ({
      outcome: tagOutcome.outcome,
      color: tagOutcome.outcome_color,
      archived: tagOutcome.archived,
      isCaseStatus: tagOutcome.is_case_status,
    })) || [],
    associatedTypes: legacyTag?.associated_types || [],
    isEphemeral: legacyTag.is_ephemeral || false,
  };
}

export function transformTagTypeAction({
  tags,
  action,
}: {
  tags: Tag[];
  action: LegacyAction;
}) {
  const matchingTagObject = tags.find(tag => tag.customer_tag_id === action.customerTagId
    && tag.tag_name.toLowerCase() === action.name.toLowerCase()) || {} as Tag;
  let issues = {};

  const tagData = getFormattedTagData(matchingTagObject, action);

  if (!matchingTagObject) {
    issues = {
      type: WorkflowTransformationIssueType.NO_MATCHING_TAG_FOUND_IN_CUSTOMER,
    };
  }

  return {
    tagData,
    issues,
  };
}

function getListServiceName(
  actionOptionsForAction: Record<string, ActionOption>,
  lists: List[],
): string {
  const listIdOption = actionOptionsForAction.customer_list_id;
  if (listIdOption) {
    const listIdOfTheAction = listIdOption.value;
    const matchingList = lists.find(list => list.customer_list_id === Number(listIdOfTheAction));

    return matchingList?.list_name || 'List';
  }

  return 'List';
}

function getCustomListServiceName(
  actionOptionsForAction: Record<string, ActionOption>,
  customLists: CustomList[],
): string {
  const listTokenOption = actionOptionsForAction.custom_list_token;
  if (listTokenOption) {
    const listTokenOfAction = listTokenOption.value;
    const matchingList = customLists.find(list => list.custom_list_token === listTokenOfAction);

    return matchingList?.name || 'Custom List';
  }

  return 'Custom List';
}

export function transformServiceTypeAction({
  services,
  action,
  actionOptionsForThisAction,
  lists,
}: {
  services: Record<number, Record<
  string, string | number | object | null
  >>;
  action: LegacyAction;
  actionOptionsForThisAction: LegacyActionOption[];
  lists: List[];
}) {
  const matchingServiceObject = Object.values(services).find(
    service => service.id === action.serviceId);
  let issues = {};

  const serviceData: ServiceObjectInAction = {
    name: matchingServiceObject?.name as string,
    internalName: matchingServiceObject?.internal_name as string,
    credentialOwner: action.credentialOwner,
  };

  if (!matchingServiceObject) {
    issues = {
      type: WorkflowTransformationIssueType.NO_MATCHING_SERVICE_FOUND_IN_CUSTOMER,
    };
  }

  if (!matchingServiceObject?.enabled) {
    issues = {
      type: WorkflowTransformationIssueType.MATCHING_SERVICE_DISABLED_FOR_CUSTOMER,
    };
  }

  if (matchingServiceObject?.name === 'List' && actionOptionsForThisAction.length <= 0) {
    issues = {
      type: WorkflowTransformationIssueType.NO_MATCHING_LIST_ID_IN_CUSTOMER,
    };
  }

  const isListService = matchingServiceObject?.internal_name === LIST_SERVICE_KEY_IN_SERVICES;
  if (isListService) {
    const listId = actionOptionsForThisAction.find(option => option.key === 'customer_list_id')?.value;
    const listItem = lists.find(l => l.customer_list_id === Number(listId));
    if (listItem?.is_whitelist) {
      serviceData.isWhitelist = true;
    } else if (listItem?.is_blacklist) {
      serviceData.isBlacklist = true;
    }
  }

  return {
    serviceData,
    issues,
  };
}

export function transformOutcomeTypeAction({
  outcomes,
  action,
}: {
  outcomes: Outcome[];
  action: LegacyAction;
}) {
  const matchingOutcomeObject = outcomes.find(outcome => outcome.customer_outcome_id
    === action.customerOutcomeId);
  let issues = {};

  const outcomeData: OutcomeObjectInAction = {
    name: matchingOutcomeObject?.outcome || action.name,
    isCaseStatus: matchingOutcomeObject?.is_case_status,
    color: matchingOutcomeObject?.outcome_color,
    icon: matchingOutcomeObject?.icon_name,
    precedenceIndex: action.precedence,
    isEphemeral: false,
  };

  if (!matchingOutcomeObject) {
    issues = {
      type: WorkflowTransformationIssueType.NO_MATCHING_OUTCOME_FOUND_IN_CUSTOMER,
    };
  }

  return {
    outcomeData,
    issues,
  };
}

export function transformInputOutputTypeAction({
  action,
  actionOptions,
  publishedAttributes,
}: {
  action: LegacyAction;
  actionOptions: LegacyActionOption[];
  publishedAttributes: PublishedAttribute[] | [];
}) {
  const matchingInputAttrActionOptionData:
  LegacyNestedInputAttributeActionOption = {} as
    LegacyNestedInputAttributeActionOption;
  const matchingOutputAttrActionOptionData:
  LegacyNestedOutputAttributeActionOption = {} as
    LegacyNestedOutputAttributeActionOption;

  if (actionOptions) {
    actionOptions.forEach(option => {
      if (inputOutputAttributeActionOptionKeys.includes(option.key)) {
        matchingInputAttrActionOptionData[option.key] = option.value;
        matchingOutputAttrActionOptionData[option.key] = option.value;
        matchingInputAttrActionOptionData.publicId = option.publicId;
        matchingOutputAttrActionOptionData.publicId = option.publicId;
      }
      if (outputAttributeActionOptionKeys.includes(option.key)) {
        matchingOutputAttrActionOptionData.publicId = option.publicId;
        matchingOutputAttrActionOptionData[option.key] = option.key === 'expression'
          ? option.jsonValue
          : option.value;
      }
    });
  }

  /*
  If action is a published attribute, build its action options from scratch,
  since its action options are not saved in handleSaveApplicationVersion
  */
  if (publishedAttributes.length > 0 && action?.publishedAttributeToken) {
    const associatedPublishedAttribute = publishedAttributes
      .find(({ attributeToken: paToken }) => (
        action?.publishedAttributeToken === paToken
      ));

    if (associatedPublishedAttribute) {
      const {
        attributeName: name,
        attributeToken: token,
        dataType,
        jqFilter = '',
        hasJqFilter,
      } = associatedPublishedAttribute;


      /*
      For published attrs used as outputs, JQ values are saved on the workflow itself, so they don't need to be added here.
      */
      if (action.type === 'output') {
        const outputPublishedAttrData: OutputObjectInAction = {
          actionOptionPublicId: generatePublicId('actionoption'),
          publishedAttributeToken: token,
          name,
          jqFunction: matchingOutputAttrActionOptionData.jq_function || '',
          dataType,
          useJq: String(matchingOutputAttrActionOptionData.use_jq),
          expression: detokenize(matchingOutputAttrActionOptionData.expression ?? []),
          publishedDetails: associatedPublishedAttribute,
        };

        return { type: action.type, outputAttrData: outputPublishedAttrData };
      }

      /*
      For published attrs used as inputs, JQ values are pulled from the published attribute itself.
      */
      if (action.type === 'input') {
        const inputPublishedAttrData: InputObjectInAction = {
          actionOptionPublicId: generatePublicId('actionoption'),
          publishedAttributeToken: token,
          name,
          jqFunction: jqFilter,
          dataType,
          useJq: String(hasJqFilter),
          publishedDetails: associatedPublishedAttribute,
        };

        return { type: action.type, inputAttrData: inputPublishedAttrData };
      }
    }
  }

  if (action.type === 'input') {
    const inputAttrData = {
      actionOptionPublicId: matchingInputAttrActionOptionData.publicId
        ? matchingInputAttrActionOptionData.publicId : generatePublicId('actionoption'),
      publishedAttributeToken: action.publishedAttributeToken,
      // Since Input display name feature was added in later,
      // older inputs used jq_function as the name:
      name: matchingInputAttrActionOptionData.name ?? matchingInputAttrActionOptionData.jq_function,
      jqFunction: matchingInputAttrActionOptionData.jq_function,
      dataType: matchingInputAttrActionOptionData.data_type,
      useJq: matchingInputAttrActionOptionData.use_jq || 'false', // if this action option is missing, we default to false
    };

    return { type: action.type, inputAttrData };
  }

  if (action.type === 'output') {
    const outputAttrData = {
      actionOptionPublicId: matchingOutputAttrActionOptionData.publicId
        ? matchingOutputAttrActionOptionData.publicId : generatePublicId('actionoption'),
      dataType: matchingOutputAttrActionOptionData.data_type || 'decimal', //  we default it to decimal if data_type is missing
      publishedAttributeToken: action.publishedAttributeToken,
      name: matchingOutputAttrActionOptionData.name,
      jqFunction: matchingOutputAttrActionOptionData.jq_function,
      useJq: matchingOutputAttrActionOptionData.use_jq || 'false', // if this action option is missing, we default to false
      expression: detokenize(matchingOutputAttrActionOptionData.expression),
    };

    return { type: action.type, outputAttrData };
  }

  return { type: WorkflowTransformationIssueType.NO_TYPE_MATCHED };
}

export function transformMatrixModelTypeAction({
  action,
  actionOptions,
  matrixModels,
}: {
  action: LegacyAction;
  actionOptions: LegacyActionOption[];
  matrixModels: LegacyMatrixModelResponse[];
}) {
  const matchingActionOptionData:
  LegacyNestedMatrixModelActionOption = {} as
    LegacyNestedMatrixModelActionOption;

  if (actionOptions) {
    actionOptions.forEach(option => {
      if (matrixModelActionOptionKeys.includes(option.key)) {
        matchingActionOptionData[option.key] = option.value;
        matchingActionOptionData.publicId = option.publicId;
      }
    });
  }

  const matrix = matrixModels.find(({ actionId }) => actionId === action.id);

  const actionData = {
    name: matrix.name,
    actionOptionPublicId: matchingActionOptionData.publicId
      ? matchingActionOptionData.publicId : generatePublicId('actionoption'),
    matrixModelPublicVersion: matchingActionOptionData.matrix_model_public_version,
    dataRouteX: matchingActionOptionData.data_route_x,
    dataRouteY: matchingActionOptionData.data_route_y,
    matrixModelId: matrix.matrix_model_id,
    matrixModelToken: matchingActionOptionData.matrix_model_token,
    activeMatrixModelVersionId: matrix.active_matrix_model_version_id,
    axis: {
      x: {
        order: getAxisOrder(
          matrix.x_axis_header_data_type,
          matrix.x_axis_header,
        ),
        axisHeaderDataType: matrix.x_axis_header_data_type,
        axisHeader: matrix.x_axis_header,
      },
      y: {
        order: getAxisOrder(
          matrix.y_axis_header_data_type,
          matrix.y_axis_header,
        ),
        axisHeaderDataType: matrix.y_axis_header_data_type,
        axisHeader: matrix.y_axis_header,
      },
    },
    matrixDataType: matrix.matrix_data_type,
    // Some previously saved matrix models may have invalid matrix values.
    // In order to prevent errors during save, we need to handle these cases on load since
    // it's possible the user will not edit the invalid matrix model after loading the workflow.
    matrix: formatEmptyMatrixValuesByType(matrix.matrix, matrix.matrix_data_type),
  };

  return actionData;
}

export function transformImportedDataPassingTypeAction({
  actionOptions,
  tags,
}: {
  actionOptions: LegacyActionOption[];
  tags: Tag[];
}): ImportedDataPassingObject {
  const matchingActionOptionData:
  LegacyImportedDataPassingActionOption = {} as
    LegacyImportedDataPassingActionOption;

  if (actionOptions) {
    actionOptions.forEach(option => {
      if (importedDataPassingActionOptionKeys.includes(option.key)) {
        matchingActionOptionData[option.key] = option.value;
        matchingActionOptionData.publicId = option.publicId;
      }
    });
  }

  const matchingData = tags.find(tag => tag.tag_name === matchingActionOptionData.display_name);
  const actionData = {
    name: matchingActionOptionData.name || '',
    actionOptionPublicId: matchingActionOptionData.publicId
      ? matchingActionOptionData.publicId : generatePublicId('actionoption'),
    displayName: matchingActionOptionData.display_name || '',
    dataType: matchingActionOptionData.data_type || '',
    useJq: matchingActionOptionData.use_jq || 'false', // set to a default value (matches reverse transformation)
    color: matchingData?.tag_color,
  };

  return actionData;
}

export function transformWorkflowTypeAction({
  legacyWorkflowAction,
}: {
  legacyWorkflowAction: LegacyAction;
}): WorkflowObjectInAction {
  return {
    name: legacyWorkflowAction.name,
    precedenceIndex: legacyWorkflowAction.precedence,
    workflowId: legacyWorkflowAction.applicationId,
  };
}

export function transformExitTypeAction({
  legacyExitAction,
}: {
  legacyExitAction: LegacyAction;
}): ExitObjectInAction {
  return {
    name: legacyExitAction.name,
  };
}

export function transformSnippetTypeAction({
  actionOptions,
  snippets,
}: {
  actionOptions: LegacyActionOption[];
  snippets: WorkflowProps['snippets'];
}): SnippetObjectInAction {
  const snippetTokenActionOption = actionOptions
    .find(ao => ao.key === SNIPPET_TOKEN_KEY);

  const publicVersionActionOption = actionOptions
    .find(ao => ao.key === SNIPPET_PUBLIC_VERSION_NUMBER_KEY);

  if (!publicVersionActionOption || !snippetTokenActionOption) {
    throw new Error('Missing required action options');
  }

  if (
    // guard against publicId being nullish
    (Boolean(publicVersionActionOption.publicId) && Boolean(snippetTokenActionOption.publicId))
    && publicVersionActionOption.publicId !== snippetTokenActionOption.publicId
  ) {
    throw new Error('Encountered mismatch in action option PublicIds');
  }

  // it does not matter which ActionOption we grab this from as their publicId value is the same
  const actionOptionPublicId = publicVersionActionOption.publicId;

  const snippetName = snippets[snippetTokenActionOption.value as string]?.name;

  return {
    name: snippetName,
    publicVersion: Number(publicVersionActionOption.value),
    token: String(snippetTokenActionOption.value),
    actionOptionPublicId,
  };
}

function transformActionOption({
  actionOption,
  action,
  lists,
}) {
  let transformedActionOptionsForAction = {};
  const issues = [];
  actionOption.forEach(option => {
    if (![
      ...inputOutputAttributeActionOptionKeys,
      ...outputAttributeActionOptionKeys,
      ...matrixModelActionOptionKeys,
      ...importedDataPassingActionOptionKeys,
      ...snippetChildActionOptionKeys,
    ].includes(option.key)) {
      let optionValue = option.value;
      if (option.key === 'customer_list_id') {
        const remappedListId = remapListIdsForListServiceAction({
          actionOptionValue: option,
          action,
          lists,
        });

        if (remappedListId.value) {
          // value will be there on both remapped/not remapped scenarios
          optionValue = remappedListId.value;
        }

        if (remappedListId.type) {
          // if type is not null, it means there was an issue or remapping has happened
          issues.push({
            publicId: action.publicId,
            type: remappedListId.type,
            manualResolution: true,
            data: {
              [option.key]: option.value,
            },
          });
        }
      }

      transformedActionOptionsForAction = {
        ...transformedActionOptionsForAction || {},
        [option.key]: {
          publicId: option.publicId
            ? option.publicId : generatePublicId('actionoption'),
          value: optionValue,
          jsonValue: option.jsonValue,
          serviceConfigOptionId: option.serviceConfigOptionId,
          selectedValues: option.selectedValues,
        },
      };
    }
  });

  return {
    transformedActionOptionsForAction,
    issues,
  };
}

export function transformLegacyActionsToJsonRepresentation({
  actions,
  actionOptions,
  tags,
  services,
  outcomes,
  publishedAttributes,
  matrixModels,
  lists,
  customLists,
  snippets,
}: {
  actions: LegacyAction[];
  actionOptions: LegacyActionOption[];
  tags: Tag[];
  services: Record<number, Record<
  string, string | number | object | null
  >>;
  outcomes: Outcome[];
  publishedAttributes: PublishedAttribute[] | [];
  matrixModels: LegacyMatrixModelResponse[];
  lists: List[];
  customLists: CustomList[];
  snippets: WorkflowProps['snippets'];
}) {
  const transformedJsonActions = {};
  let issuesWithTransformedJsonActions = [];
  const legacyActionsToTransformedActionsIdMap: {
    relativeIds: Record<string, string>;
    absoluteIds: Record<string, string>;
  } = {
    relativeIds: {},
    absoluteIds: {},
  };

  actions.forEach(action => {
    let actionType = action.type;

    if (action.type === 'run') {
      actionType = ActionType.SERVICE;
    }

    const objectPublicId = action.publicId && !action.publicId.includes('run') // @todo: `run` string check is a temporary code
      ? action.publicId : generatePublicId('action', actionType as ActionType);

    const transformedActionObject: Action = {
      publicId: objectPublicId,
      type: actionType as ActionType,
      serviceFailureDefault: action.serviceFailureDefault,
      options: {},
    };

    legacyActionsToTransformedActionsIdMap.relativeIds[action.id] = objectPublicId;
    legacyActionsToTransformedActionsIdMap.absoluteIds[action.actionId] = objectPublicId;

    const actionOptionsForThisAction = actionOptions.filter(
      option => option.actionId === action.id);

    if (actionType === ActionType.TAG) {
      const { tagData, issues }: {
        tagData: TagObjectInAction;
        issues: TransformIssue;
      } = transformTagTypeAction({
        tags,
        action,
      });
      transformedActionObject.tag = tagData;

      if (issues?.type) {
        issuesWithTransformedJsonActions.push({
          publicId: objectPublicId,
          manualResolution: false,
          type: issues?.type,
          data: {
            tag: { id: action.customerTagId, ...tagData },
          },
        });
      }
    }

    if (actionType === ActionType.SERVICE) {
      const { serviceData, issues }: {
        serviceData: ServiceObjectInAction;
        issues: TransformIssue;
      } = transformServiceTypeAction({
        services,
        action,
        actionOptionsForThisAction,
        lists,
      });

      transformedActionObject.service = serviceData;

      if (issues?.type) {
        issuesWithTransformedJsonActions.push({
          publicId: objectPublicId,
          manualResolution: issues?.type
            === WorkflowTransformationIssueType.NO_MATCHING_LIST_ID_IN_CUSTOMER,
          type: issues?.type,
          data: {
            service: { id: action.serviceId, ...serviceData },
          },
        });
      }
    }

    if (actionType === ActionType.OUTCOME) {
      const { outcomeData, issues }: {
        outcomeData: OutcomeObjectInAction;
        issues: TransformIssue;
      } = transformOutcomeTypeAction({
        outcomes,
        action,
      });

      transformedActionObject.outcome = outcomeData;

      if (issues?.type) {
        issuesWithTransformedJsonActions.push({
          publicId: objectPublicId,
          manualResolution: false,
          type: issues?.type,
          data: {
            outcome: { id: action.customerOutcomeId, ...outcomeData },
          },
        });
      }
    }

    if ([
      ActionType.INPUT as string,
      ActionType.OUTPUT as string,
    ].includes(actionType)) {
      const { type, inputAttrData, outputAttrData }: {
        type: string;
        inputAttrData?: InputObjectInAction;
        outputAttrData?: OutputObjectInAction;
      } = transformInputOutputTypeAction({
        action,
        actionOptions: actionOptionsForThisAction,
        publishedAttributes,
      });

      if (type === ActionType.INPUT) {
        transformedActionObject.input = inputAttrData;
      }

      if (type === ActionType.OUTPUT) {
        transformedActionObject.output = outputAttrData;
      }
    }

    if (actionType === ActionType.MATRIX_MODEL) {
      const matrixActionData = transformMatrixModelTypeAction({
        action,
        actionOptions: actionOptionsForThisAction,
        matrixModels,
      });

      transformedActionObject.matrixModel = matrixActionData;
    }

    if (actionType === ActionType.OUTCOME_REASON
      || actionType === ActionType.IMPORTED_OUTPUT_ATTRIBUTE
      || actionType === ActionType.IMPORTED_MATRIX_MODEL) {
      const importedDataPassingActionData = transformImportedDataPassingTypeAction({
        actionOptions: actionOptionsForThisAction,
        tags,
      });

      switch (actionType) {
        case ActionType.OUTCOME_REASON:
          transformedActionObject.outcomeReason = importedDataPassingActionData;
          break;
        case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
          transformedActionObject.importedOutputAttribute = importedDataPassingActionData;
          break;
        case ActionType.IMPORTED_MATRIX_MODEL:
          transformedActionObject.importedMatrixModel = importedDataPassingActionData;
          break;
      }
    }

    if (actionType === ActionType.WORKFLOW) {
      const workflowActionData = transformWorkflowTypeAction({
        legacyWorkflowAction: action,
      });

      transformedActionObject.workflow = workflowActionData;
    }

    if (actionType === ActionType.EXIT) {
      const exitActionData = transformExitTypeAction({
        legacyExitAction: action,
      });

      transformedActionObject.exit = exitActionData;
    }

    if (actionType === ActionType.SNIPPET) {
      const snippetActionData = transformSnippetTypeAction({
        actionOptions: actionOptionsForThisAction,
        snippets,
      });
      transformedActionObject.snippet = snippetActionData;
    }

    transformedActionObject.rules = action.booleanRule;

    if (actionOptionsForThisAction?.length) {
      const { transformedActionOptionsForAction, issues } = transformActionOption({
        actionOption: actionOptionsForThisAction,
        action: transformedActionObject,
        lists,
      });

      transformedActionObject.options = transformedActionOptionsForAction;

      if (containsSnippetRemoteActionMap(actionOptionsForThisAction)) {
        transformedActionObject.containerContext = createContainerContextForActionFromOptions({
          actionOptions: actionOptionsForThisAction,
        });
      }

      if (issues.length) {
        issuesWithTransformedJsonActions = [
          ...issuesWithTransformedJsonActions,
          ...issues,
        ];
      }
    } else {
      transformedActionObject.options = {};
    }

    if (actionType === ActionType.SNIPPET) {
      transformedActionObject.options = {};
    }

    if (actionType === ActionType.SERVICE) {
      switch (transformedActionObject.service?.name) {
        case 'List':
          transformedActionObject.service.name = getListServiceName(
            transformedActionObject.options, lists);
          break;
        case 'Custom List':
          transformedActionObject.service.name = getCustomListServiceName(
            transformedActionObject.options, customLists);

          if (!transformedActionObject.options?.[CUSTOM_LIST_COLUMNS_OPTION_KEY]) {
            const customListToken = transformedActionObject
              .options?.[CUSTOM_LIST_TOKEN_OPTION_KEY]?.value;
            const majorVersionValue = transformedActionObject
              .options?.[CUSTOM_LIST_VERSION_OPTION_KEY]?.value;

            const customList = customLists.find(list => list.custom_list_token === customListToken);
            const majorVersion = customList?.versions.find(
              version => version.major_version === Number(majorVersionValue),
            );

            if (majorVersion) {
              transformedActionObject.options[CUSTOM_LIST_COLUMNS_OPTION_KEY] = {
                publicId: generatePublicId('actionoption'),
                value: '',
                jsonValue: majorVersion.columns,
                serviceConfigOptionId: null,
                selectedValues: null,
              };
            }
          }
          break;
      }
    }

    transformedJsonActions[objectPublicId] = { ...transformedActionObject };
  });

  return {
    transformedActionsWithoutRules: transformedJsonActions,
    legacyActionsTransformationMap: legacyActionsToTransformedActionsIdMap,
    issuesWithTransformedJsonActions,
  };
}

export function transformBooleanRuleAndDataRoutesInActions({
  transformedActionsWithoutRules,
  legacyToTransformedThresholdIdMap,
  legacyDataRoutesToTransformedDataRoutesIdMap,
}: {
  transformedActionsWithoutRules: Record<string, Action>;
  legacyToTransformedThresholdIdMap: ObjectIdTransformationMap;
  legacyDataRoutesToTransformedDataRoutesIdMap: ObjectIdTransformationMap;
}) {
  const transformedActionsWithRules = {};

  Object.entries(transformedActionsWithoutRules).forEach(([actionPublicId, actionObjectValue]) => {
    const transformedBooleanRules = transformBooleanRule({
      booleanRule: actionObjectValue.rules as string,
      legacyToTransformedThresholdIdMap,
    });

    if (actionObjectValue.type === ActionType.OUTPUT) {
      actionObjectValue.output.expression = remapExpressionDataRouteIds({
        expressionArray: actionObjectValue.output.expression,
        dataRouteIdMap: legacyDataRoutesToTransformedDataRoutesIdMap,
      });
    }

    if (actionObjectValue.type === ActionType.MATRIX_MODEL) {
      actionObjectValue.matrixModel.dataRouteX = legacyDataRoutesToTransformedDataRoutesIdMap[
        actionObjectValue.matrixModel.dataRouteX
      ];
      actionObjectValue.matrixModel.dataRouteY = legacyDataRoutesToTransformedDataRoutesIdMap[
        actionObjectValue.matrixModel.dataRouteY
      ];
    }

    transformedActionsWithRules[actionPublicId] = {
      ...actionObjectValue,
      rules: transformedBooleanRules,
    };
  });

  return transformedActionsWithRules;
}
